/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import { useState, useEffect, useCallback } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

const useGetServicingPayment = (loanNumber: string) => {
  const [servicingLoanPayments, setServicingLoanPayments] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const { token } = useAuthContext();

  const { reset } = useAbortController();

  const getServicingPayment = useCallback(async () => {
    if (!token || !loanNumber) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const url = process.env.REACT_APP_SERVICING_PAYMENT.replace('?', loanNumber);

    const controller = reset();
    const config = { signal: controller.signal };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    try {
      const response = await axiosCall({ url: url, method: 'get' }, config, headers);
      setServicingLoanPayments(response);
      setLoading(false);
    } catch (error) {
      if (error.code !== 'ERR_CANCELED') {
        setErrorMessage('Error Loading Payments.');
        setLoading(false);
        console.error(error.message);
      }
    }
  }, [loanNumber, reset, token]);

  useEffect(() => {
    getServicingPayment();
  }, [getServicingPayment]);

  return {
    servicingLoanPayments: servicingLoanPayments,
    servicingErrorMessages: errorMessage,
    loadingServicing: loading,
    refetch: getServicingPayment,
  };
};

export default useGetServicingPayment;
